import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { Target as A1, CheckSquare as A2, Calendar as A3, Flag as A4, Repeat as A5, TrendingUp as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Target, Plus, GripVertical, CheckCircle, Clock, Archive,
  Trash2, Edit3, ChevronRight, Star, Calendar, Tag, X,
  Briefcase, GraduationCap, DollarSign, BookOpen, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PlanBoard from '../components/plan/PlanBoard';
import TaskDrawer from '../components/plan/TaskDrawer';
import { COLUMNS as PLAN_COLUMNS } from '../lib/planConstants';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Goal categories with icons
const CATEGORIES = {
  career: { label: 'Career', icon: Briefcase, color: 'blue' },
  college: { label: 'College', icon: GraduationCap, color: 'purple' },
  scholarship: { label: 'Scholarship', icon: DollarSign, color: 'green' },
  skill: { label: 'Skill', icon: Zap, color: 'amber' },
  certification: { label: 'Certification', icon: BookOpen, color: 'cyan' },
  personal: { label: 'Personal', icon: Star, color: 'pink' },
};

const COLUMNS = PLAN_COLUMNS.map(c => ({ ...c, color: 'slate' }));

export default function GoalsPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    category: 'personal',
    priority: 'medium',
    target_date: '',
    source: 'manual'
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setGoals(response.data.goals || []);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const addGoal = async () => {
    if (!newGoal.title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }

    try {
      const response = await axios.post(`${API}/goals`, {
        ...newGoal,
        status: 'recommended'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setGoals([...goals, response.data]);
      window.dispatchEvent(new Event('goals-updated'));
      setShowAddModal(false);
      setNewGoal({ title: '', description: '', category: 'personal', priority: 'medium', target_date: '', source: 'manual' });
      toast.success('Goal added to your board!');
    } catch (error) {
      toast.error('Failed to add goal');
    }
  };

  const updateGoalStatus = async (goalId, newStatus) => {
    try {
      await axios.put(`${API}/goals/${goalId}/status`, { status: newStatus }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setGoals(goals.map(g => g.id === goalId ? { ...g, status: newStatus } : g));
      window.dispatchEvent(new Event('goals-updated'));
      
      if (newStatus === 'completed') {
        celebrate('Goal complete. That is one less thing between you and the target.');
      } else if (newStatus === 'in_progress') {
        toast.success('Goal moved to In Progress');
      }
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    
    try {
      await axios.delete(`${API}/goals/${goalId}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setGoals(goals.filter(g => g.id !== goalId));
      window.dispatchEvent(new Event('goals-updated'));
      toast.success('Goal deleted');
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const getGoalsByStatus = (status) => {
    return goals.filter(g => g.status === status);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'medium': return 'text-amber-400 bg-amber-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      default: return 'text-slate-300 bg-slate-500/20';
    }
  };

  if (loading) {
    return (
      <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="goals" /></div>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  const totalGoals = goals.length;
  const completedGoals = getGoalsByStatus('completed').length;

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="goals-arrival"
        eyebrow="Goals & Actions"
        title="Big intentions, broken into moves."
        subtitle="A goal you cannot act on this week is a wish. Here each one gets split into actions small enough to finish, with a date and a reason it matters."
        accent="#34d399"
        second="#fbbf24"
        ctaLabel="Set my goals"
        narration={"This is Goals and Actions. A goal that cannot be acted on this week is just a wish, so every goal here gets broken into steps small enough to actually finish, each with a date and a reason it matters. Tick them off and you can see the goal move, which is the part that keeps people going."}
        facts={[
          { icon: A2, head: "Actions, not wishes", sub: "Small enough to finish this week" },
          { icon: A3, head: "Dated", sub: "Every action has a when" },
          { icon: A6, head: "Visible progress", sub: "Watch the goal move as you go" }
        ]}
        modules={[
          { icon: A1, label: "Your goals", line: "What you are actually trying to achieve", hue: "#34d399" },
          { icon: A2, label: "Break it down", line: "Each goal split into finishable steps", hue: "#22d3ee" },
          { icon: A3, label: "This week", line: "What to do now, not eventually", hue: "#fbbf24" },
          { icon: A5, label: "Habits", line: "The small repeated things that compound", hue: "#a78bfa" },
          { icon: A4, label: "Wins", line: "A record of what you have finished", hue: "#f472b6" },
          { icon: A6, label: "Momentum", line: "Streaks and progress kept in front of you", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white font-outfit flex items-center gap-3">
            <Target className="w-8 h-8 text-blue-400" />
            My Goals
          </h1>
          <p className="text-slate-300 mt-1">Your personal Kanban board for college & career success</p>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-goal-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Goal
        </Button>
      </div>

      {/* Welcome Banner - Show when no goals */}
      {totalGoals === 0 && (
        <GlassCard className="mb-8 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-500/30">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Target className="w-10 h-10 text-blue-400" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-xl font-bold text-white mb-2">Welcome to Your Goals Board! 🎯</h2>
              <p className="text-slate-300 mb-4">
                This is your personal Kanban board to track everything from college applications to career milestones.
                Think of it like Trello or Asana - but designed specifically for your educational journey.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Goal
                </Button>
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      {/* How It Works - Collapsible */}
      {totalGoals <= 3 && (
        <GlassCard className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-amber-400" />
            <h3 className="font-semibold text-white">How to Use Your Goals Board</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-navy-900/50 rounded-lg border-l-4 border-slate-500">
              <div className="flex items-center gap-2 mb-2">
                <Archive className="w-5 h-5 text-slate-300" />
                <span className="font-medium text-white">Recommended</span>
              </div>
              <p className="text-sm text-slate-300">
                Add goals you want to accomplish. These are your recommended items - everything from "Apply to MIT" to "Get HVAC Certified".
              </p>
            </div>
            <div className="p-4 bg-navy-900/50 rounded-lg border-l-4 border-blue-500">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-blue-400" />
                <span className="font-medium text-white">In Progress</span>
              </div>
              <p className="text-sm text-slate-300">
                Drag goals here when you're actively working on them. Focus on 2-3 items at a time for best results.
              </p>
            </div>
            <div className="p-4 bg-navy-900/50 rounded-lg border-l-4 border-green-500">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-medium text-white">Done</span>
              </div>
              <p className="text-sm text-slate-300">
                Celebrate your wins! Completed goals stay here as a record of your achievements.
              </p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-900/30 rounded-lg border border-blue-500/30">
            <p className="text-sm text-blue-300">
              <Star className="w-4 h-4 inline mr-1" />
              <strong>Pro Tip:</strong> Add goals from anywhere on FuturePath! Look for the "Add to Goals" button on Career Paths, Colleges, Programs, and more.
            </p>
          </div>
        </GlassCard>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {COLUMNS.map(col => {
          const Icon = col.icon;
          const count = getGoalsByStatus(col.id).length;
          return (
            <GlassCard key={col.id} className="text-center">
              <Icon className={`w-5 h-5 mx-auto mb-2 ${col.text}`} />
              <p className="text-2xl font-bold text-white">{count}</p>
              <p className="text-xs text-slate-300">{col.label}</p>
            </GlassCard>
          );
        })}
      </div>

      {/* Kanban Board, shared with the FuturePath Plan */}
      <PlanBoard
        tasks={goals}
        onOpenTask={setOpenTask}
        onStatusChange={(task, status) => updateGoalStatus(task.id, status)}
      />

      {openTask && (
        <TaskDrawer
          task={goals.find(g => g.id === openTask.id) || openTask}
          allTasks={goals}
          authHeader={getAuthHeader()}
          onClose={() => setOpenTask(null)}
          onChanged={() => { fetchGoals(); setOpenTask(null); }}
          onAskCoach={() => { setOpenTask(null); window.location.assign('/app/my-plan'); }}
        />
      )}


      {/* Add Goal Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-[#0a1729]/60 flex items-center justify-center z-50 p-4">
          <div className="bg-navy-800 rounded-xl max-w-md w-full p-6 border border-navy-600">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Add New Goal</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Goal Title *</label>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                  className="w-full bg-navy-900 border border-navy-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-blue-500"
                  placeholder="e.g., Apply to MIT, Learn Python, Get HVAC Certified"
                  data-testid="goal-title-input"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2">Description</label>
                <textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                  className="w-full bg-navy-900 border border-navy-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-blue-500 h-20"
                  placeholder="Add details about this goal..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">Category</label>
                  <select
                    value={newGoal.category}
                    onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })}
                    className="w-full bg-navy-900 border border-navy-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-blue-500"
                  >
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                      <option key={key} value={key}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-slate-300 mb-2">Priority</label>
                  <select
                    value={newGoal.priority}
                    onChange={(e) => setNewGoal({ ...newGoal, priority: e.target.value })}
                    className="w-full bg-navy-900 border border-navy-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2">Target Date (optional)</label>
                <input
                  type="date"
                  value={newGoal.target_date}
                  onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })}
                  className="w-full bg-navy-900 border border-navy-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border-navy-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={addGoal}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-goal-btn"
              >
                Add to Board
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
